// CR151: Issue Severity Matrix Editor component
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RotateCcw, Plus, Trash2 } from "lucide-react";
import type { IssueSeverityConfig, IssueSeverity } from "@/lib/deal-policy/types";

interface IssueSeverityEditorProps {
  severityMap: Record<string, IssueSeverityConfig>;
  onChange: (map: Record<string, IssueSeverityConfig>) => void;
}

const DEFAULT_SEVERITY_MAP: Record<string, IssueSeverityConfig> = {
  MISSING_PROMO_MERCHANT: {
    severity: "HARD_BLOCK",
    blocks_dedup: true,
    blocks_publish: true,
    description: "Promo merchant name is required",
  },
  MISSING_REWARD_BRAND_FOR_GIFTCARD: {
    severity: "HARD_BLOCK",
    blocks_dedup: true,
    blocks_publish: true,
    description: "Reward brand is required for gift card deals",
  },
  MISSING_DATES: {
    severity: "WARNING",
    blocks_dedup: false,
    blocks_publish: false,
    description: "Start or end date is missing",
  },
  MISSING_VALUE: {
    severity: "WARNING",
    blocks_dedup: false,
    blocks_publish: false,
    description: "Benefit value could not be determined",
  },
  LOW_CONFIDENCE_EXTRACTION: {
    severity: "WARNING",
    blocks_dedup: false,
    blocks_publish: false,
    description: "AI extraction confidence is below threshold",
  },
  AMBIGUOUS_TERMS: {
    severity: "WARNING",
    blocks_dedup: false,
    blocks_publish: false,
    description: "Deal terms are ambiguous or unclear",
  },
  EXPIRED_DEAL: {
    severity: "WARNING",
    blocks_dedup: false,
    blocks_publish: true,
    description: "Deal end date has passed",
  },
  FUTURE_DEAL: {
    severity: "INFO",
    blocks_dedup: false,
    blocks_publish: false,
    description: "Deal has not started yet",
  },
};

const SEVERITY_COLORS: Record<IssueSeverity, string> = {
  HARD_BLOCK: "bg-destructive text-destructive-foreground",
  WARNING: "bg-yellow-100 text-yellow-800",
  INFO: "bg-blue-100 text-blue-800",
};

export function IssueSeverityEditor({ severityMap, onChange }: IssueSeverityEditorProps) {
  const updateIssue = (key: string, updates: Partial<IssueSeverityConfig>) => {
    onChange({
      ...severityMap,
      [key]: { ...severityMap[key], ...updates },
    });
  };

  const deleteIssue = (key: string) => {
    const { [key]: _, ...rest } = severityMap;
    onChange(rest);
  };

  const addIssue = () => {
    const newKey = `CUSTOM_ISSUE_${Date.now()}`;
    onChange({
      ...severityMap,
      [newKey]: {
        severity: "WARNING",
        blocks_dedup: false,
        blocks_publish: false,
        description: "New custom issue",
      },
    });
  };

  const restoreDefaults = () => {
    onChange(DEFAULT_SEVERITY_MAP);
  };

  const sortedEntries = Object.entries(severityMap).sort((a, b) => {
    const severityOrder = { HARD_BLOCK: 0, WARNING: 1, INFO: 2 };
    return severityOrder[a[1].severity] - severityOrder[b[1].severity];
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Configure which issues block deduplication or publishing.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={restoreDefaults}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Restore Defaults
          </Button>
          <Button size="sm" variant="outline" onClick={addIssue}>
            <Plus className="h-4 w-4 mr-1" />
            Add Issue
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Issue Key</TableHead>
              <TableHead className="w-32">Severity</TableHead>
              <TableHead className="w-28">Blocks Dedup</TableHead>
              <TableHead className="w-28">Blocks Publish</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedEntries.map(([key, config]) => (
              <TableRow key={key}>
                <TableCell className="font-mono text-sm">{key}</TableCell>
                <TableCell>
                  <Select
                    value={config.severity}
                    onValueChange={(v) => updateIssue(key, { severity: v as IssueSeverity })}
                  >
                    <SelectTrigger className="w-28">
                      <Badge className={SEVERITY_COLORS[config.severity]}>
                        {config.severity}
                      </Badge>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HARD_BLOCK">HARD_BLOCK</SelectItem>
                      <SelectItem value="WARNING">WARNING</SelectItem>
                      <SelectItem value="INFO">INFO</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={config.blocks_dedup}
                    onCheckedChange={(checked) => updateIssue(key, { blocks_dedup: checked })}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={config.blocks_publish}
                    onCheckedChange={(checked) => updateIssue(key, { blocks_publish: checked })}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={config.description}
                    onChange={(e) => updateIssue(key, { description: e.target.value })}
                    className="text-sm"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => deleteIssue(key)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
